import { useEffect, useState } from "react";
import axios from "axios";
import Stars from "./Stars";
import { ShieldCheck, Star, PenSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORTS = [
  { v: "newest", label: "Newest" },
  { v: "highest", label: "Highest rated" },
  { v: "lowest", label: "Lowest rated" },
  { v: "oldest", label: "Oldest" },
];

const ReviewsSection = ({ tyreId, avgRating, onCountChange }) => {
  const [data, setData] = useState({ items: [], total: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, title: "", comment: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/tyres/${tyreId}/reviews`, { params: { sort } });
      setData(d);
      onCountChange?.(d.total);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tyreId, sort]);

  const submit = async (e) => {
    e.preventDefault(); setMsg(""); setErr("");
    if (form.comment.trim().length < 10) { setErr("Please write at least 10 characters."); return; }
    if (form.name.trim().length < 2) { setErr("Please enter your name (2 characters min)."); return; }
    setSubmitting(true);
    try {
      const payload = { name: form.name.trim(), rating: Number(form.rating), comment: form.comment.trim() };
      if (form.title.trim()) payload.title = form.title.trim();
      if (form.email.trim()) payload.email = form.email.trim();
      await axios.post(`${API}/tyres/${tyreId}/reviews`, payload);
      setMsg("Thanks! Your review has been published.");
      setForm({ name: "", rating: 5, title: "", comment: "", email: "" });
      setShowForm(false);
      load();
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : (Array.isArray(detail) ? detail[0]?.msg : "Could not submit review."));
    } finally { setSubmitting(false); }
  };

  const total = data.total;
  const dist = data.distribution || {};

  return (
    <section className="vd-card p-6 mt-10" data-testid="reviews-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="mb-1" style={{ fontSize: 24 }}>Customer reviews</h2>
          <p className="vd-muted text-sm">{total === 0 ? "Be the first to review this tyre" : `${total} review${total === 1 ? "" : "s"}`}</p>
        </div>
        <button onClick={() => setShowForm((s) => !s)} className="vd-btn-primary" data-testid="review-write-btn">
          <PenSquare size={16} /> {showForm ? "Close" : "Write a review"}
        </button>
      </div>

      {total > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6 mb-6" data-testid="review-summary">
          <div className="flex flex-col items-center justify-center text-center" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", borderRadius: 8, padding: 20 }}>
            <p className="vd-heading font-extrabold" style={{ fontSize: 44, lineHeight: 1 }} data-testid="review-avg">{(avgRating ?? 0).toFixed(1)}</p>
            <div className="mt-1"><Stars value={avgRating ?? 0} size={18} /></div>
            <p className="vd-muted text-xs mt-1">{total} review{total === 1 ? "" : "s"}</p>
          </div>
          <div className="flex flex-col gap-2" data-testid="review-dist">
            {[5, 4, 3, 2, 1].map((n) => {
              const count = dist[n] || dist[String(n)] || 0;
              const pct = total ? Math.round((count / total) * 100) : 0;
              return (
                <div key={n} className="flex items-center gap-3 text-sm" data-testid={`dist-row-${n}`}>
                  <span className="vd-muted w-6">{n}★</span>
                  <div className="flex-1 rounded-full overflow-hidden" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", height: 8 }}>
                    <div style={{ width: `${pct}%`, background: "var(--primary-red)", height: "100%", transition: "width .3s" }} />
                  </div>
                  <span className="vd-muted text-xs w-12 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mb-6 p-4 rounded-md" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)" }} data-testid="review-form">
          <p className="vd-heading font-semibold mb-3">Share your experience</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="vd-input" placeholder="Your name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rf-name" />
            <input type="email" className="vd-input" placeholder="Email (optional — for Verified Buyer badge)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="rf-email" />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <span className="vd-muted text-xs uppercase tracking-wider">Your rating</span>
            <div className="flex items-center gap-1" data-testid="rf-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} className="p-0.5" aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`} data-testid={`rf-star-${n}`}>
                  <Star size={22} fill={n <= form.rating ? "var(--primary-red)" : "transparent"} color="var(--primary-red)" />
                </button>
              ))}
            </div>
          </div>
          <input className="vd-input mt-3" placeholder="Review title (optional)" maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="rf-title" />
          <textarea className="vd-input mt-3" rows={4} placeholder="Tell other drivers what you thought — handling, grip, noise, longevity, value… (min 10 chars)" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="rf-comment" />
          {err && <p className="text-xs mt-2" style={{ color: "var(--primary-red)" }} data-testid="rf-err">{err}</p>}
          {msg && <p className="text-xs mt-2" style={{ color: "#00A35C" }} data-testid="rf-msg">{msg}</p>}
          <div className="flex gap-2 mt-4">
            <button type="submit" disabled={submitting} className="vd-btn-primary" data-testid="rf-submit">{submitting ? "Submitting…" : "Post review"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="vd-btn-secondary" data-testid="rf-cancel">Cancel</button>
          </div>
        </form>
      )}

      <div className="flex items-center justify-between mb-3">
        <select className="vd-select" style={{ width: "auto" }} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="review-sort">
          {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="vd-muted text-sm">Loading reviews…</p>
      ) : data.items.length === 0 ? (
        <div className="vd-card p-8 text-center" data-testid="reviews-empty">
          <p className="vd-heading font-semibold mb-1">No reviews yet</p>
          <p className="vd-muted text-sm">Be the first to share your experience with this tyre.</p>
        </div>
      ) : (
        <ul className="flex flex-col divide-y" style={{ borderColor: "var(--border-color)" }}>
          {data.items.map((r) => (
            <li key={r.id} className="py-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Stars value={r.rating} size={14} />
                {r.title && <p className="vd-heading font-semibold" style={{ fontSize: 15 }}>{r.title}</p>}
              </div>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <p className="vd-heading font-medium text-sm">{r.name}</p>
                {r.verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded px-1.5 py-0.5" style={{ background: "rgba(0,180,80,0.15)", color: "#00A35C" }} data-testid={`review-verified-${r.id}`}>
                    <ShieldCheck size={11} /> Verified Buyer
                  </span>
                )}
                <span className="vd-muted text-xs">· {new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <p className="vd-body text-sm whitespace-pre-line">{r.comment}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ReviewsSection;
